import { Injectable, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs';
import { NetworkState } from '../app/shared/models/network-health.model';
import { NetworkHealthService } from './network-health.service';

/**
 * NetworkNotificationService
 *
 * Listens to NetworkHealthService and fires PrimeNG toast notifications
 * for non-NORMAL network state transitions.
 *
 * Anti-spam rules:
 * 1. Each state transition fires AT MOST one toast.
 * 2. NORMAL state = no toast ever (not even "All is well").
 * 3. RECOVERING = brief "Connection restored." toast (3 s, auto-dismiss) — only once.
 * 4. If the state returns to a previously notified state, we DO notify again
 *    (e.g., goes OFFLINE → NORMAL → OFFLINE again = new toast).
 * 5. Billing-specific messages (SLOW_WARN, CRITICAL during order) are managed
 *    separately by DashboardComponent + BillingGuardService — NOT here.
 *
 * Toast key: 'network' — uses its own <p-toast key="network"> in layout.html
 * to keep network toasts visually separate from business toasts.
 */
@Injectable({ providedIn: 'root' })
export class NetworkNotificationService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /** The last state we showed a toast for. Used to avoid repeat toasts. */
  private lastNotifiedState: NetworkState | null = null;

  private static readonly TOAST_KEY = 'network';

  constructor(
    private readonly networkHealthService: NetworkHealthService,
    private readonly messageService: MessageService
  ) {
    this.subscribeToStateChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  private subscribeToStateChanges(): void {
    this.networkHealthService.networkState$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((state) => {
        this.handleStateChange(state);
      });
  }

  private handleStateChange(state: NetworkState): void {
    // NORMAL → clear any lingering network toasts, show nothing new
    if (state === NetworkState.NORMAL) {
      if (this.lastNotifiedState !== null && this.lastNotifiedState !== NetworkState.NORMAL) {
        this.clearNetworkToasts();
      }
      this.lastNotifiedState = NetworkState.NORMAL;
      return;
    }

    // Avoid showing the same notification again (no repeated "Network is slow" spam)
    if (state === this.lastNotifiedState) {
      return;
    }

    this.lastNotifiedState = state;
    this.clearNetworkToasts(); // Clear previous network toast before showing new one

    switch (state) {
      case NetworkState.DEGRADED:
        // Silent — no toast for DEGRADED, only the indicator pill changes
        break;

      case NetworkState.SLOW:
        // Slow-network toast disabled
        // this.messageService.add({
        //   key: NetworkNotificationService.TOAST_KEY,
        //   severity: 'warn',
        //   summary: 'Network Slow',
        //   detail: 'Process Order cannot be completed while the network connection is slow. Please check your connection and try again.',
        //   sticky: true,
        //   closable: true,
        // });
        break;

      case NetworkState.OFFLINE:
        this.messageService.add({
          key: NetworkNotificationService.TOAST_KEY,
          severity: 'error',
          summary: 'No Connection',
          detail: 'No network connection. Please check your internet and try again.',
          sticky: true,
          closable: true,
        });
        break;

      case NetworkState.RECOVERING:
        // "Connection restored" — shown briefly, auto-dismissed
        this.messageService.add({
          key: NetworkNotificationService.TOAST_KEY,
          severity: 'success',
          summary: 'Connection Restored',
          detail: 'Connection restored.',
          life: 3000,
          closable: true,
        });
        break;

      case NetworkState.CRITICAL:
        // CRITICAL is managed by billing-specific messages in DashboardComponent
        // We don't add a global toast here to avoid doubling up
        break;
    }
  }

  private clearNetworkToasts(): void {
    this.messageService.clear(NetworkNotificationService.TOAST_KEY);
  }
}
